// @ts-nocheck
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // <-- add this import
import { renderTime } from 'utils/formatter';
import daLocale from '@fullcalendar/core/locales/da';

const eventClassName = 'penly-meeting-event';

const MeetingsCalendar = ({ meetings }) => {
    const navigate = useNavigate();
    // Get organisationId from URL params
    const organisationId = window.location.pathname.split('/')[1];

    // Inject event hover styles
    React.useEffect(() => {
        const styleId = 'penly-meeting-event-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .${eventClassName} {
                    cursor: pointer;
                    transition: opacity 0.2s;
                }
                .${eventClassName}:hover {
                    opacity: 0.95;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Prepare events for FullCalendar
    const events = useMemo(() => {
        if (!meetings) return [];
        return meetings.map((meeting) => {
            const { booking } = meeting;
            const { order } = meeting;
            const customerName = order?.customer ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() : '';
            const advisorName = booking?.vendorsAttendees?.join(', ') || '';
            const products = order?.items?.map(i => i.product?.name).filter(Boolean).join(', ') || '';
            const start = booking?.startTime || booking?.calendlyEvent?.start_time;
            const end = booking?.endTime || booking?.calendlyEvent?.end_time;
            const orderId = order?.orderId;
            const orderPrefix = orderId ? `#${order?.orderNumber}: ` : '';
            const completed = !!order?.dataCollectionProgress?.completed; // <-- get completed flag
            return {
                id: orderId || booking?.eventId,
                title: `${orderPrefix}${customerName} / ${advisorName}\n${products}`,
                start,
                end,
                extendedProps: {
                    orderId,
                    completed, // <-- pass completed flag
                },
                backgroundColor: '#009094',
                borderColor: '#009094',
                textColor: '#fff',
            };
        });
    }, [meetings]);

    // Set initial date to today
    const initialDate = useMemo(() => {
        const now = new Date();
        return now.toISOString().slice(0, 10);
    }, []);

    // Limit calendar to only show 4 weeks ahead
    const validRange = useMemo(() => {
        const start = new Date();
        return {
            start: start.toISOString().slice(0, 10),
        };
    }, []);

    const handleEventClick = (info) => {
        const { orderId } = info.event.extendedProps;
        if (orderId && organisationId) {
            navigate(`/${organisationId}/access/orders/${orderId}`);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <h1 style={{  marginBottom: 40 }}>
                Møder 4 uger frem
            </h1>
            <FullCalendar
                plugins={ [dayGridPlugin, interactionPlugin] }
                initialView="custom4Week"
                initialDate={ initialDate }
                locale={ daLocale }
                views={{
                    custom4Week: {
                        type: 'dayGrid',
                        duration: { weeks: 5 },
                        buttonText: '4 uger',

                    },
                }}
                validRange={ validRange }
                events={ events }
                eventClick={ handleEventClick }
                height="auto"
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: '',
                }}
                eventContent={ renderEventContent }
                eventClassNames={ () => eventClassName }
            />
        </Box>
    );
};

// Custom rendering for event content
function renderEventContent(eventInfo) {
    const [customerAndAdvisor, products] = eventInfo.event.title.split('\n');
    const startTime = eventInfo.event.start;
    const endTime = eventInfo.event.end;
    const { completed } = eventInfo.event.extendedProps; // <-- get completed flag
    let timeString = '';
    if (startTime && endTime) {
        timeString = `${renderTime(startTime)} - ${renderTime(endTime)}`;
    } else if (startTime) {
        timeString = renderTime(startTime);
    }
    return (
        <div
            style={{
                background: '#009094',
                width: '100%',
                color: '#fff',
                borderRadius: 4,
                padding: 4,
                minHeight: 40,
                boxSizing: 'border-box',
                fontSize: 13,
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflow: 'hidden',
                position: 'relative', // <-- add for icon positioning
            }}
        >
            <div style={{
                fontWeight: 500,
                wordBreak: 'break-word',
                whiteSpace: 'normal',
            }}>
                { customerAndAdvisor }
            </div>
            <div style={{ fontSize: 12, color: '#e0f7f7', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                { products }
            </div>
            { timeString && (
                <div style={{ fontSize: 12, color: '#fff', marginTop: 2 }}>
                    { timeString }
                </div>
            ) }
            { completed && (
                <CheckCircleIcon
                    sx={{
                        color: 'rgb(239, 151, 97)', // <-- updated color
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        fontSize: 20,
                        background: '#009094',
                        borderRadius: '50%',
                    }}
                />
            ) }
        </div>
    );
}

export default MeetingsCalendar;
